import React, { ReactNode } from 'react';
import { useDensity } from '../../../hooks/useDensity';

export type GroupLayout = 'horizontal' | 'vertical' | 'mixed' | 'responsive';
export type GroupAlignment = 'start' | 'center' | 'end' | 'stretch';
export type GroupSpacing = 'tight' | 'normal' | 'loose';

export interface FormFieldGroupProps {
  children: ReactNode;
  title?: string | ReactNode;
  description?: string;
  layout?: GroupLayout;
  alignment?: GroupAlignment;
  spacing?: GroupSpacing;
  wrap?: boolean;
  equalWidth?: boolean;
  className?: string;
  groupClassName?: string;
  headerClassName?: string;
  border?: boolean;
  collapsible?: boolean;
  defaultExpanded?: boolean;
  required?: boolean;
  error?: string;
  actions?: ReactNode;
}

const FormFieldGroup: React.FC<FormFieldGroupProps> = ({
  children,
  title,
  description,
  layout = 'responsive',
  alignment = 'start',
  spacing = 'normal',
  wrap = true,
  equalWidth = false,
  className = '',
  groupClassName = '',
  headerClassName = '',
  border = false,
  collapsible = false,
  defaultExpanded = true,
  required = false,
  error,
  actions,
}) => {
  const { classes, isCompact, isNormal } = useDensity();
  const [isExpanded, setIsExpanded] = React.useState(defaultExpanded);

  // Determine effective layout based on screen size and content
  const effectiveLayout = React.useMemo(() => {
    if (layout !== 'responsive') return layout;

    const screenWidth = window.innerWidth;
    const childCount = React.Children.count(children);

    // Auto-detect best layout
    if (screenWidth < 768 || isCompact || childCount === 1) {
      return 'vertical';
    } else if (childCount <= 3) {
      return 'horizontal';
    } else {
      return 'mixed';
    }
  }, [layout, children, isCompact]);

  // Generate spacing classes
  const spacingClasses = React.useMemo(() => {
    const spacingMap = {
      tight: isCompact ? 'gap-1' : 'gap-2',
      normal: isCompact ? 'gap-2' : isNormal ? 'gap-3' : 'gap-4',
      loose: isCompact ? 'gap-3' : isNormal ? 'gap-4' : 'gap-6',
    };

    return spacingMap[spacing];
  }, [spacing, isCompact, isNormal]);

  // Generate alignment classes
  const alignmentClasses = React.useMemo(() => {
    const alignMap = {
      start: 'items-start',
      center: 'items-center',
      end: 'items-end',
      stretch: 'items-stretch',
    };

    return alignMap[alignment];
  }, [alignment]);

  // Container classes based on layout
  const containerClasses = React.useMemo(() => {
    const baseClasses = 'form-field-group w-full';
    const spacingClass = classes(spacingClasses);

    switch (effectiveLayout) {
      case 'horizontal':
        return classes(
          [
            baseClasses,
            'flex',
            alignmentClasses,
            spacingClass,
            wrap ? 'flex-wrap' : 'flex-nowrap',
            groupClassName,
          ]
            .filter(Boolean)
            .join(' ')
        );

      case 'vertical':
        return classes(
          [baseClasses, 'flex flex-col', spacingClass, groupClassName].filter(Boolean).join(' ')
        );

      case 'mixed':
        // Mixed layout: first field full width, rest horizontal
        return classes([baseClasses, 'space-y-2', groupClassName].filter(Boolean).join(' '));

      default:
        return classes([baseClasses, spacingClass, groupClassName].filter(Boolean).join(' '));
    }
  }, [effectiveLayout, alignmentClasses, spacingClasses, wrap, classes, groupClassName]);

  // Field wrapper classes for equal width
  const fieldWrapperClasses = React.useMemo(() => {
    if (!equalWidth || effectiveLayout === 'vertical') return '';

    return classes('flex-1 min-w-0');
  }, [equalWidth, effectiveLayout, classes]);

  // Wrapper classes for entire component
  const wrapperClasses = classes(
    [
      'form-field-group-wrapper',
      border ? 'border border-gray-200 rounded-lg' : '',
      border ? (isCompact ? 'p-2' : isNormal ? 'p-3' : 'p-4') : '',
      className,
    ]
      .filter(Boolean)
      .join(' ')
  );

  // Header classes
  const headerClasses = classes(
    [
      'form-field-group-header',
      border ? 'border-b border-gray-100 mb-2 pb-2' : 'mb-2',
      headerClassName,
    ]
      .filter(Boolean)
      .join(' ')
  );

  const titleClasses = classes(
    ['font-semibold text-gray-900', isCompact ? 'text-xs' : isNormal ? 'text-sm' : 'text-base']
      .filter(Boolean)
      .join(' ')
  );

  const descriptionClasses = classes(
    ['text-gray-600 mt-1', isCompact ? 'text-xs' : 'text-sm'].filter(Boolean).join(' ')
  );

  // Process children based on layout
  const processedChildren = React.useMemo(() => {
    const childArray = React.Children.toArray(children);

    if (effectiveLayout === 'mixed' && childArray.length > 1) {
      const [firstChild, ...restChildren] = childArray;

      return (
        <>
          {/* First field takes full width */}
          <div className="w-full">
            {React.isValidElement(firstChild) && equalWidth
              ? React.cloneElement(firstChild as React.ReactElement<any>, {
                  className: fieldWrapperClasses,
                })
              : firstChild}
          </div>

          {/* Rest of fields in horizontal layout */}
          {restChildren.length > 0 && (
            <div
              className={classes(
                ['flex', alignmentClasses, spacingClasses, wrap ? 'flex-wrap' : 'flex-nowrap']
                  .filter(Boolean)
                  .join(' ')
              )}
            >
              {restChildren.map((child, index) => (
                <div key={index} className={fieldWrapperClasses}>
                  {child}
                </div>
              ))}
            </div>
          )}
        </>
      );
    }

    // For other layouts, wrap children if equalWidth is enabled
    if (equalWidth && (effectiveLayout === 'horizontal' || effectiveLayout === 'vertical')) {
      return childArray.map((child, index) => (
        <div key={index} className={fieldWrapperClasses}>
          {child}
        </div>
      ));
    }

    return children;
  }, [
    children,
    effectiveLayout,
    equalWidth,
    fieldWrapperClasses,
    alignmentClasses,
    spacingClasses,
    wrap,
    classes,
  ]);

  // Toggle expansion for collapsible groups
  const toggleExpansion = () => {
    if (collapsible) {
      setIsExpanded(!isExpanded);
    }
  };

  return (
    <div className={wrapperClasses}>
      {/* Header */}
      {(title || description || actions) && (
        <div className={headerClasses}>
          <div
            className={classes(
              [
                'flex items-center',
                actions ? 'justify-between' : '',
                collapsible ? 'cursor-pointer' : '',
              ]
                .filter(Boolean)
                .join(' ')
            )}
            onClick={toggleExpansion}
          >
            <div className="flex items-center">
              {title && (
                <h3 className={titleClasses}>
                  {title}
                  {required && <span className="text-red-500 ml-1">*</span>}
                </h3>
              )}

              {collapsible && (
                <button
                  type="button"
                  className={classes(
                    [
                      'ml-2 text-gray-400 hover:text-gray-600 transition-transform',
                      isExpanded ? '' : 'rotate-180',
                    ]
                      .filter(Boolean)
                      .join(' ')
                  )}
                  aria-label={isExpanded ? 'Collapse section' : 'Expand section'}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
              )}
            </div>

            {actions && <div className="flex items-center">{actions}</div>}
          </div>

          {description && <p className={descriptionClasses}>{description}</p>}
        </div>
      )}

      {/* Content */}
      {(!collapsible || isExpanded) && <div className={containerClasses}>{processedChildren}</div>}

      {/* Error message */}
      {error && <div className={classes('text-red-600 text-xs mt-2')}>{error}</div>}
    </div>
  );
};

// Preset configurations for common field groupings
export const FieldGroupPresets = {
  // Name fields (first, last, etc.)
  name: {
    title: 'Name',
    layout: 'horizontal' as GroupLayout,
    equalWidth: true,
    spacing: 'normal' as GroupSpacing,
  },

  // Address fields
  address: {
    title: 'Address',
    layout: 'mixed' as GroupLayout,
    spacing: 'normal' as GroupSpacing,
    border: true,
  },

  // Contact information
  contact: {
    title: 'Contact Information',
    layout: 'responsive' as GroupLayout,
    spacing: 'normal' as GroupSpacing,
    border: true,
  },

  // Financial fields (down payment, monthly payment, etc.)
  financial: {
    title: 'Financial Details',
    layout: 'horizontal' as GroupLayout,
    equalWidth: true,
    spacing: 'tight' as GroupSpacing,
    border: true,
  },

  // Vehicle details
  vehicle: {
    title: 'Vehicle Information',
    layout: 'responsive' as GroupLayout,
    spacing: 'normal' as GroupSpacing,
    collapsible: true,
    border: true,
  },

  // Advanced options (typically collapsible)
  advanced: {
    title: 'Advanced Options',
    layout: 'vertical' as GroupLayout,
    spacing: 'normal' as GroupSpacing,
    collapsible: true,
    defaultExpanded: false,
    border: true,
  },

  // Quick filters (horizontal, compact)
  filters: {
    layout: 'horizontal' as GroupLayout,
    spacing: 'tight' as GroupSpacing,
    wrap: true,
    alignment: 'center' as GroupAlignment,
  },
};

export default FormFieldGroup;
